import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, AlertCircle, Package, Copy, Check } from 'lucide-react';
import api from '../../services/api';
import Button from '../../components/shared/Button/Button';
import Input from '../../components/shared/Input/Input';
import StatusBadge from '../../components/shared/StatusBadge/StatusBadge';
import Stepper, { Step } from '../../components/shared/Stepper/Stepper';
import type { Parcel, ParcelStatus } from '../../types/api';

const TRACK_STEPS: Step[] = [
  { label: 'Received' },
  { label: 'In transit' },
  { label: 'Arrived' },
  { label: 'Delivered' },
  { label: 'Picked' },
];

const STATUS_TO_STEP: Record<ParcelStatus, number> = {
  received: 0,
  in_transit: 1,
  arrived: 2,
  delivered: 3,
  picked: 4,
  cancelled: 0,
};

const TrackParcel: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const [code, setCode] = useState(params.get('code') ?? '');
  const [parcel, setParcel] = useState<Parcel | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const performTrack = async (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    setLoading(true);
    setError('');
    setParcel(null);
    try {
      const res = await api.get<Parcel>(`/track/${trimmed}/`);
      setParcel(res.data);
    } catch {
      setError('Parcel not found. Please check the tracking code and try again.');
    }
    setLoading(false);
  };

  useEffect(() => {
    const queryCode = params.get('code');
    if (queryCode) {
      performTrack(queryCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setParams(code.trim() ? { code: code.trim() } : {}, { replace: true });
    performTrack(code);
  };

  const handleCopy = async () => {
    if (!parcel) return;
    await navigator.clipboard.writeText(parcel.tracking_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const currentStep = parcel ? STATUS_TO_STEP[parcel.status] : 0;

  return (
    <div className="mx-auto max-w-3xl px-4 py-10 sm:px-6 sm:py-14">
      <div className="text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
          <Package className="h-6 w-6" aria-hidden />
        </div>
        <h1 className="mt-4 text-3xl font-bold tracking-tight text-slate-900">
          Track a parcel
        </h1>
        <p className="mt-2 text-base text-slate-600">
          Enter your tracking code to see live status and details.
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="mt-8 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:p-5"
      >
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="PCL-XXXXXXXX"
          aria-label="Tracking code"
          className="font-mono uppercase"
          leadingIcon={<Search className="h-4 w-4" />}
          autoFocus
        />
        <Button type="submit" size="lg" loading={loading}>
          {loading ? 'Tracking…' : 'Track parcel'}
        </Button>
      </form>

      {error && (
        <div className="mt-6 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{error}</span>
        </div>
      )}

      {parcel && (
        <article className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <header className="flex flex-col gap-4 border-b border-slate-200 px-6 py-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Tracking code
              </p>
              <div className="mt-1 flex items-center gap-2">
                <h2 className="font-mono text-2xl font-bold tracking-tight text-slate-900">
                  {parcel.tracking_code}
                </h2>
                <button
                  type="button"
                  onClick={handleCopy}
                  aria-label="Copy tracking code"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-emerald-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
              </div>
              {parcel.trip_route && (
                <p className="mt-1 text-base text-slate-600">{parcel.trip_route}</p>
              )}
            </div>
            <StatusBadge variant="parcel" status={parcel.status} />
          </header>

          {parcel.status !== 'cancelled' && (
            <div className="border-b border-slate-200 px-6 py-7">
              <Stepper steps={TRACK_STEPS} currentStep={currentStep} />
            </div>
          )}

          <dl className="grid grid-cols-1 gap-x-8 gap-y-4 px-6 py-6 sm:grid-cols-2">
            <Detail label="Sender" value={parcel.sender_name} />
            <Detail label="Sender phone" value={parcel.sender_phone} />
            <Detail label="Recipient" value={parcel.recipient_name} />
            <Detail label="Recipient phone" value={parcel.recipient_phone} />
            <Detail label="Description" value={parcel.description || '—'} />
            <Detail
              label="Created"
              value={new Date(parcel.created_at).toLocaleString()}
            />
          </dl>
        </article>
      )}
    </div>
  );
};

const Detail: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <dt className="text-xs font-semibold uppercase tracking-wider text-slate-500">
      {label}
    </dt>
    <dd className="mt-1 text-base text-slate-900">{value}</dd>
  </div>
);

export default TrackParcel;
